import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { normalizeRole } from '../utils/userLocale';
import { apiJson } from '../apiClient';
import {
  downloadCsv,
  exportInvoicePdf,
  exportOrdersListPdf,
  exportProformaPdf,
  exportPurchaseOrderPdf,
} from '../utils/reportExport';
import { brandingFromUser } from '../utils/tenantBranding';
import './OrdersList.css';
import { FaCheckCircle, FaTimesCircle, FaEye } from 'react-icons/fa';
import OrderDetailsModal from './OrderDetailsModal';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending approval' },
  { key: 'approved', label: 'Approved' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'partially_received', label: 'Partially received' },
  { key: 'received', label: 'Received' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
];

const ORDERS_CSV_HEAD = [
  'po_number',
  'order_id',
  'order_date_utc',
  'status',
  'requested_by',
  'supplier_name',
  'supplier_phone',
  'priority',
  'needed_by_date',
  'payment_terms',
  'payment_due_date',
  'estimated_total_tzs',
  'order_notes',
  'product_name',
  'category',
  'quantity',
  'unit_cost_tzs',
  'line_total_tzs',
  'line_reason',
];

/** @returns {any[][]} one row per product line (full detail for CSV export). */
function buildOrderLineItemRows(orders) {
  const rows = [];
  for (const order of orders) {
    const products = order.product_requests || order.productRequests || [];
    const by = order.user?.name || order.user?.email || '';
    const supplierName = order.supplier_name || order.supplier?.name || '';
    const supplierPhone = order.supplier_phone || order.supplier?.phone || '';
    const priority = order.priority || '';
    const neededBy = order.needed_by_date || '';
    const paymentTerms = order.payment_terms || '';
    const paymentDueDate = order.payment_due_date || '';
    const orderTotal = order.estimated_total_tzs != null ? String(order.estimated_total_tzs) : '';
    const date = order.created_at != null ? String(order.created_at) : '';
    const st = order.status || '';
    const poNumber = order.po_number || '';
    const notes = order.notes != null ? String(order.notes) : '';
    if (products.length === 0) {
      rows.push([
        poNumber,
        order.id,
        date,
        st,
        by,
        supplierName,
        supplierPhone,
        priority,
        neededBy,
        paymentTerms,
        paymentDueDate,
        orderTotal,
        notes,
        '',
        '',
        '',
        '',
        '',
      ]);
    } else {
      for (const p of products) {
        rows.push([
          poNumber,
          order.id,
          date,
          st,
          by,
          supplierName,
          supplierPhone,
          priority,
          neededBy,
          paymentTerms,
          paymentDueDate,
          orderTotal,
          notes,
          p.product_name || '',
          p.category != null ? String(p.category) : '',
          p.quantity != null ? String(p.quantity) : '',
          p.estimated_unit_cost_tzs != null ? String(p.estimated_unit_cost_tzs) : '',
          p.estimated_line_total_tzs != null ? String(p.estimated_line_total_tzs) : '',
          p.reason != null ? String(p.reason) : '',
        ]);
      }
    }
  }
  return rows;
}

export default function OrdersList({ factoryTracking = false, automotiveTracking = false, onNavigate }) {
  const catalogTracking = factoryTracking || automotiveTracking;
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [actionNotice, setActionNotice] = useState(null);

  const upsertOrder = useCallback((updatedOrder) => {
    if (!updatedOrder) return;
    setOrders((prev) => prev.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)));
    setSelectedOrder((prev) => (prev && prev.id === updatedOrder.id ? updatedOrder : prev));
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiJson(isOwner ? '/orders' : '/my-orders');
      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const normalizedOrderStatus = (o) => {
    const s = o.status || 'pending_approval';
    return s === 'pending' ? 'pending_approval' : s;
  };

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    return orders.filter((o) => normalizedOrderStatus(o) === filter);
  }, [orders, filter]);

  const statusLabel = (status) => {
    const raw = String(status || 'pending_approval');
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const showActionNotice = (variant, poLabel) => {
    const isApproved = variant === 'approved';
    setActionNotice({
      variant,
      poLabel,
      title: isApproved ? 'Order imekubaliwa!' : 'Order imerekodi kama rejected',
      text: isApproved
        ? catalogTracking
          ? factoryTracking
            ? 'Material order imeidhinishwa. Unaweza kuifuatilia hapa chini kwenye Tracking status.'
            : 'Purchase order imeidhinishwa. Unaweza kuifuatilia hapa chini kwenye Pokea na fuatilia.'
          : 'Purchase order imeidhinishwa.'
        : catalogTracking
          ? factoryTracking
            ? 'Material order haijakubaliwa. Status imesasishwa kwenye orodha.'
            : 'Purchase order haijakubaliwa. Status imesasishwa kwenye orodha.'
          : 'Purchase order imerekodi kama rejected.',
    });
  };

  const handleNoticeOk = () => {
    setActionNotice(null);
    if (typeof onNavigate === 'function') {
      onNavigate(catalogTracking ? 'orders-tracking' : 'orders-list');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleApprove = async (id) => {
    setError('');
    setMessage('');
    setProcessingId(id);
    try {
      const data = await apiJson(`/orders/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ status: 'approved', comment: 'PO approved for procurement.' }),
      });
      if (data?.order) {
        setOrders((prev) => prev.map((o) => (o.id === data.order.id ? data.order : o)));
      } else {
        await fetchOrders();
      }
      setSelectedOrder(null);
      showActionNotice('approved', data?.order?.po_number || `PO-${id}`);
    } catch (err) {
      setError(err.body?.message || err.message || 'Approve failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id) => {
    setError('');
    setMessage('');
    setProcessingId(id);
    try {
      const data = await apiJson(`/orders/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ status: 'rejected', comment: 'PO rejected after review.' }),
      });
      if (data?.order) {
        setOrders((prev) => prev.map((o) => (o.id === data.order.id ? data.order : o)));
      } else {
        await fetchOrders();
      }
      setSelectedOrder(null);
      showActionNotice('rejected', data?.order?.po_number || `PO-${id}`);
    } catch (err) {
      setError(err.body?.message || err.message || 'Reject failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleStatusUpdate = useCallback(
    async (id, status, comment) => {
      setError('');
      setMessage('');
      setProcessingId(id);
      try {
        const data = await apiJson(`/orders/${id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status, comment }),
        });
        upsertOrder(data.order);
        setMessage(`Order moved to ${statusLabel(status)}.`);
      } catch (err) {
        setError(err.body?.message || err.message || 'Status update failed');
      } finally {
        setProcessingId(null);
      }
    },
    [upsertOrder]
  );

  const handleReceive = useCallback(
    async (id, payload) => {
      setError('');
      setMessage('');
      setProcessingId(id);
      try {
        const data = await apiJson(`/orders/${id}/receive`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        upsertOrder(data.order);
        setMessage(
          `Received ${Number(data.received_now_qty || 0).toLocaleString()} units (TZS ${Math.round(
            Number(data.received_now_tzs || 0)
          ).toLocaleString()}).`
        );
      } catch (err) {
        setError(err.body?.message || err.message || 'Receiving failed');
      } finally {
        setProcessingId(null);
      }
    },
    [upsertOrder]
  );

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const handleDownloadOrders = useCallback(() => {
    if (!isOwner) {
      setError('Only owner can export reports.');
      return;
    }
    if (filteredOrders.length === 0) return;
    const data = buildOrderLineItemRows(filteredOrders);
    const tag = filter === 'all' ? 'all' : filter;
    downloadCsv(`orders-${tag}-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`, [
      ORDERS_CSV_HEAD,
      ...data,
    ]);
  }, [filteredOrders, filter, isOwner]);

  const handleDownloadOrdersPdf = useCallback(async () => {
    if (!isOwner) {
      setError('Only owner can export reports.');
      return;
    }
    if (filteredOrders.length === 0 || pdfExporting) return;
    setPdfExporting(true);
    setError('');
    setMessage('');
    try {
      const filterLabel = FILTERS.find((f) => f.key === filter)?.label || filter;
      const reportTitle =
        catalogTracking && factoryTracking && !automotiveTracking ? 'Material orders' : 'Purchase orders';

      await exportOrdersListPdf(filteredOrders, {
        filterLabel,
        filter,
        title: reportTitle,
        branding: brandingFromUser(user),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError('Could not create PDF. Check your connection and try again.');
    } finally {
      setPdfExporting(false);
    }
  }, [filteredOrders, filter, pdfExporting, isOwner, catalogTracking, factoryTracking, automotiveTracking, user]);

  return (
    <div className="orders-list-shell">
      <div className="orders-table-container">
        <div className="orders-list-head">
          <h2 className="orders-list-title">
            {automotiveTracking
              ? 'Purchase order tracking'
              : factoryTracking
                ? 'Material order tracking'
                : 'Purchase order list'}
          </h2>
          <div className="orders-list-actions">
            {isOwner ? (
              <>
                <button
                  type="button"
                  className="orders-download-btn"
                  onClick={handleDownloadOrders}
                  disabled={loading || filteredOrders.length === 0 || pdfExporting}
                  title="Spreadsheet (CSV) — one row per line item"
                >
                  Download CSV
                </button>
                <button
                  type="button"
                  className="orders-download-pdf"
                  onClick={() => void handleDownloadOrdersPdf()}
                  disabled={loading || filteredOrders.length === 0 || pdfExporting}
                  title="PDF report — same data as CSV"
                >
                  {pdfExporting ? 'PDF…' : 'Download PDF'}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="orders-refresh-btn"
              onClick={fetchOrders}
              disabled={loading || pdfExporting}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="orders-filter-bar" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`orders-filter-pill${filter === f.key ? ' orders-filter-pill--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && <div className="orders-feedback">Loading orders…</div>}
        {!loading && error && <div className="orders-feedback error">{error}</div>}
        {!loading && message && !error && <div className="orders-feedback success">{message}</div>}

        {!loading && filteredOrders.length === 0 ? (
          <div className="orders-empty">
            <p className="orders-empty-title">
              {orders.length === 0
                ? catalogTracking
                  ? automotiveTracking
                    ? 'No purchase orders yet'
                    : 'No material orders yet'
                  : 'No orders yet'
                : 'No orders in this filter'}
            </p>
            <p className="orders-empty-text">
              {orders.length === 0
                ? catalogTracking
                  ? automotiveTracking
                    ? 'Submit a purchase order from Agiza mzigo — it will appear here for tracking and receive.'
                    : 'Submit a material order from Order materials — it will appear here for tracking and receive.'
                  : isOwner
                    ? 'When staff submit purchase requests, they will appear here.'
                    : 'Use “Create PO” to submit your first purchase order.'
                : 'Try another status filter.'}
            </p>
          </div>
        ) : (
          !loading && (
            <div className="orders-table-scroll">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Lines</th>
                    <th>Date</th>
                    {isOwner && <th>Requested by</th>}
                    <th>Total (TZS)</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const st = normalizedOrderStatus(order);
                    const products = order.product_requests || order.productRequests || [];
                    const orderLabel =
                      products.length > 0
                        ? products
                            .map((p) => `${p.product_name || ''}${p.quantity ? ` ×${p.quantity}` : ''}`)
                            .join(', ')
                        : `Order #${order.id}`;
                    const lineCount = products.length;
                    const submittedBy = order.user?.name || order.user?.email || '—';

                    return (
                      <tr key={order.id} className="orders-table-row">
                        <td className="orders-cell-main">
                          <div>{order.po_number || `PO-${order.id}`}</div>
                          <div className="orders-cell-muted">{orderLabel}</div>
                        </td>
                        <td>{lineCount ? `${lineCount} line${lineCount !== 1 ? 's' : ''}` : '—'}</td>
                        <td className="orders-cell-muted">{formatDate(order.created_at)}</td>
                        {isOwner && <td className="orders-cell-muted">{submittedBy}</td>}
                        <td className="orders-cell-muted">
                          {order.estimated_total_tzs != null
                            ? Math.round(Number(order.estimated_total_tzs) || 0).toLocaleString()
                            : '—'}
                        </td>
                        <td>
                          <span
                            className={`orders-status-badge orders-status-badge--readonly ${st}`}
                            aria-label={`Status: ${st}`}
                          >
                            {(st === 'approved' || st === 'received') && <FaCheckCircle aria-hidden />}
                            {st === 'rejected' && <FaTimesCircle aria-hidden />}
                            {statusLabel(st)}
                          </span>
                        </td>
                        <td>
                          <div className="orders-row-actions">
                            <button
                              type="button"
                              className="orders-icon-btn orders-icon-btn--primary"
                              title={
                                isOwner && (st === 'pending_approval')
                                  ? 'View full request — approve or reject inside'
                                  : 'View order details'
                              }
                              onClick={() => setSelectedOrder(order)}
                            >
                              <FaEye /> <span className="orders-icon-btn-label">View</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <OrderDetailsModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onStatusUpdate={handleStatusUpdate}
        onReceive={handleReceive}
        onDownloadPo={exportPurchaseOrderPdf}
        onDownloadProforma={exportProformaPdf}
        onDownloadInvoice={exportInvoicePdf}
        isOwner={isOwner}
        processingId={processingId}
        factoryMaterialOrder={factoryTracking}
        automotivePurchaseOrder={automotiveTracking}
      />

      {actionNotice ? (
        <div className="orders-notice-backdrop" role="presentation" onClick={handleNoticeOk}>
          <div
            className={`orders-notice-modal orders-notice-modal--${actionNotice.variant}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="orders-notice-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="orders-notice-icon" aria-hidden>
              {actionNotice.variant === 'approved' ? <FaCheckCircle size={28} /> : <FaTimesCircle size={28} />}
            </div>
            <h3 id="orders-notice-title" className="orders-notice-title">
              {actionNotice.title}
            </h3>
            {actionNotice.poLabel ? (
              <p className="orders-notice-po">{actionNotice.poLabel}</p>
            ) : null}
            <p className="orders-notice-text">{actionNotice.text}</p>
            <button type="button" className="orders-notice-btn" onClick={handleNoticeOk} autoFocus>
              {catalogTracking
                ? automotiveTracking
                  ? 'Sawa — nenda Pokea na fuatilia'
                  : 'Sawa — nenda Tracking status'
                : 'Sawa — nenda order list'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
